import 'tldraw/tldraw.css';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, DialogActions, DialogContent, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Box,
  createTLStore,
  DefaultColorThemePalette,
  Editor,
  TLAssetId,
  Tldraw,
} from '@tldraw/tldraw';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TFormType, TMainVisualFormData } from 'src/@types';
import { DeviceToggle } from 'src/components/device-toggle/DeviceToggle';
import FormProvider from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { IMainVisual } from 'src/models';
import { DEVICE_DIMENSIONS, TDeviceType } from 'src/static/constants';
import { FormType } from 'src/static/constants/common/form';
import {
  extractTextFromHtml,
  FIXED_POSITIONS,
  TLDRAW_CONFIG,
} from 'src/static/constants/initialStates/editor';
import { percentToPixel } from 'src/utils/styleUtils';

type TEditorData = {
  shapes: any[];
  assets: any[];
};
// 기존 타입 정의에 추가
export interface IDeviceEditorState {
  desktop: TEditorData;
  tablet: TEditorData;
  mobile: TEditorData;
}
interface ITestMainVisualFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IMainVisual;
}

const STORAGE_KEY = 'tldraw-device-data';

const EditorMode = ({
  formType,
  onSubmitSuccess,
  selectedData,
}: ITestMainVisualFormDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [device, setDevice] = useState<TDeviceType>('desktop');
  const [editor, setEditor] = useState<Editor | null>(null);
  // tldraw 스토어 생성
  const [store] = useState(() => createTLStore());
  /**
   * 디바이스별 데이터 상태: Tldrwa의 모양은 shapes이고 shapes는 실제 assets을 참조해서 만들어짐
   * 1. 재사용성: 같은 이미지를 여러 shape에서 참조 가능
   * 2. 성능: 큰 미디어 파일을 한 번만 로드
   * 3. 데이터 관리: 미디어 파일의 원본 정보 유지
   */
  const [deviceData, setDeviceData] = useState<IDeviceEditorState>({
    desktop: { shapes: [], assets: [] },
    tablet: { shapes: [], assets: [] },
    mobile: { shapes: [], assets: [] },
  });
  const methods = useForm<TMainVisualFormData>();

  // viewport 업데이트
  useEffect(() => {
    if (!editor) return;

    const deviceBounds = DEVICE_DIMENSIONS[device];
    editor.updateViewportScreenBounds(new Box(0, 0, deviceBounds.width, deviceBounds.height), true);
  }, [device, editor]);

  const handleMount = (editor: Editor) => {
    DefaultColorThemePalette.lightMode.orange.solid = 'white';
    setEditor(editor);
    // 현재 디바이스의 데이터 로드
    const currentDeviceData = deviceData[device];
    if (currentDeviceData.shapes.length > 0) {
      // 현재 디바이스의 assets 생성
      editor.createAssets(currentDeviceData.assets);

      // 현재 디바이스의 shapes 생성
      currentDeviceData.shapes.forEach((shape) => {
        editor.createShape(shape);
      });
    }

    // 디바이스 크기에 맞게 viewport 설정
    const deviceBounds = DEVICE_DIMENSIONS[device];
    editor.updateViewportScreenBounds(new Box(0, 0, deviceBounds.width, deviceBounds.height), true);

    // 비동기 작업을 별도 함수로 분리
    const initializeElements = async () => {
      try {
        if (formType === FormType.UPDATE && selectedData) {
          if (selectedData.bgPath) {
            const mediaUrl = Array.isArray(selectedData.bgPath)
              ? process.env.REACT_APP_API_URL + selectedData.bgPath[0]
              : process.env.REACT_APP_API_URL + selectedData.bgPath;

            try {
              const response = await fetch(mediaUrl);
              const blob = await response.blob();
              const isVideo = blob.type.startsWith('video/');

              const assetId = `asset:${mediaUrl}` as TLAssetId;
              await editor.createAssets([
                {
                  id: assetId,
                  type: isVideo ? 'video' : 'image',
                  typeName: 'asset',
                  meta: {},
                  props: {
                    name: 'background',
                    src: mediaUrl,
                    w: deviceBounds.width,
                    h: deviceBounds.height,
                    mimeType: blob.type,
                    isAnimated: isVideo,
                  },
                },
              ]);

              // 미디어 shape 생성
              const mediaPosition = FIXED_POSITIONS.getMediaPosition(device);
              editor.createShape({
                type: isVideo ? 'video' : 'image',
                x: mediaPosition.x,
                y: mediaPosition.y,
                props: {
                  assetId,
                  w: mediaPosition.w,
                  h: mediaPosition.h,
                  // src: mediaUrl,
                },
              });
            } catch (error) {
              console.error('Error creating asset:', error);
            }
          }

          // 텍스트 요소들 생성
          editor.createShape({
            type: 'text',
            x: percentToPixel(FIXED_POSITIONS.mainText.x, deviceBounds.width),
            y: percentToPixel(FIXED_POSITIONS.mainText.y, deviceBounds.height),
            props: {
              text: selectedData.mainText.text
                ? extractTextFromHtml(selectedData.mainText.text ?? '')
                : '메인 텍스트를 입력하세요',
              w: percentToPixel(FIXED_POSITIONS.mainText.width, deviceBounds.width),
            },
          });

          editor.createShape({
            type: 'text',
            x: percentToPixel(FIXED_POSITIONS.subText.x, deviceBounds.width),
            y: percentToPixel(FIXED_POSITIONS.subText.y, deviceBounds.height),
            props: {
              text: selectedData.subText.text
                ? extractTextFromHtml(selectedData.subText.text ?? '')
                : '서브 텍스트를 입력하세요',
              w: percentToPixel(FIXED_POSITIONS.subText.width, deviceBounds.width),
            },
          });
        } else {
          // CREATE 모드 코드
          editor.createShape({
            type: 'text',
            x: percentToPixel(FIXED_POSITIONS.mainText.x, deviceBounds.width),
            y: percentToPixel(FIXED_POSITIONS.mainText.y, deviceBounds.height),
            props: {
              text: '메인 텍스트를 입력하세요',
              w: percentToPixel(FIXED_POSITIONS.mainText.width, deviceBounds.width),
            },
          });

          editor.createShape({
            type: 'text',
            x: percentToPixel(FIXED_POSITIONS.subText.x, deviceBounds.width),
            y: percentToPixel(FIXED_POSITIONS.subText.y, deviceBounds.height),
            props: {
              text: '서브 텍스트를 입력하세요',
              w: percentToPixel(FIXED_POSITIONS.subText.width, deviceBounds.width),
            },
          });
        }
      } catch (error) {
        console.error('Error initializing elements:', error);
      }
    };

    // 비동기 함수 실행
    initializeElements();
  };

  // 1. 초기 데이터 로드
  useEffect(() => {
    if (formType === FormType.UPDATE && selectedData) {
      const initialData = {
        desktop:
          selectedData.mode === 'editor'
            ? selectedData.editorContent.desktop
            : { shapes: [], assets: [] },
        tablet:
          selectedData.mode === 'editor'
            ? selectedData.editorContent.tablet
            : { shapes: [], assets: [] },
        mobile:
          selectedData.mode === 'editor'
            ? selectedData.editorContent.mobile
            : { shapes: [], assets: [] },
      };
      setDeviceData(initialData);
      // localStorage에도 저장
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData));
    } else {
      // 신규 작성 시 localStorage 데이터 확인
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        setDeviceData(JSON.parse(savedData));
      }
    }
  }, [selectedData, formType]);

  // 2. 디바이스별 저장
  const handleDeviceSave = (currentDevice: TDeviceType) => {
    if (!editor) return;

    try {
      const shapes = editor.getCurrentPageShapes();
      const assets = editor.getAssets();

      setDeviceData((prev) => {
        const newData = {
          ...prev,
          [currentDevice]: { shapes, assets },
        };

        // localStorage에 저장
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));

        return newData;
      });

      enqueueSnackbar(`${currentDevice} 저장되었습니다.`, { variant: 'success' });
    } catch (error) {
      console.error('Error saving device data:', error);
      enqueueSnackbar('저장 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  // 2-1. 디바이스 변경 시 데이터 로드
  const handleDeviceChange = async (
    event: React.MouseEvent<HTMLElement>,
    newDevice: TDeviceType,
  ) => {
    if (!editor || newDevice === device) return;
    if (newDevice) setDevice(newDevice);

    // 새 디바이스의 데이터 로드
    const newDeviceData = deviceData[newDevice];
    if (newDeviceData.shapes.length > 0) {
      // 새 디바이스의 assets 생성
      await editor.createAssets(newDeviceData.assets);

      // 새 디바이스의 shapes 생성
      newDeviceData.shapes.forEach((shape) => {
        editor.createShape(shape);
      });
    }

    // 새 디바이스 크기에 맞게 viewport 업데이트
    const deviceBounds = DEVICE_DIMENSIONS[newDevice];
    editor.updateViewportScreenBounds(new Box(0, 0, deviceBounds.width, deviceBounds.height), true);
  };

  // 3. 게시하기 전체 저장
  const onSubmit = () => {
    if (!editor) return;

    try {
      console.log(deviceData);
      console.log(JSON.stringify(deviceData));
      // 모든 shape 가져오기
      const shapes = editor.getCurrentPageShapes();
      const snapshot = editor.getSnapshot();
      // 성공 콜백 호출
      // onSubmitSuccess();
    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <RootContent dividers>
        <ControlBar>
          <ControlBarInner direction="row" alignItems="center" spacing={2}>
            {/* 좌측: 디바이스 정보 */}
            <DeviceInfo variant="caption" color="text.secondary">
              {device.charAt(0).toUpperCase() + device.slice(1)}
            </DeviceInfo>

            {/* 중앙: 디바이스 토글 */}
            <Stack flex={1} alignItems="center">
              <DeviceToggle device={device} onDeviceChange={handleDeviceChange} />
            </Stack>

            {/* 우측: 저장 버튼 */}
            <Tooltip
              title="현재 디바이스의 변경사항을 저장합니다. 각 디바이스별로 저장해야 내용이 반영됩니다."
              arrow
              placement="bottom"
            >
              <Button
                onClick={() => handleDeviceSave(device)}
                variant="contained"
                color="warning"
                startIcon={<Iconify icon="mdi:content-save" />}
                size="small"
              >
                {device}
              </Button>
            </Tooltip>
          </ControlBarInner>
        </ControlBar>

        {/* 에디터 */}
        <EditorContainer>
          <EditorWrapper>
            <div
              className="tldraw__editor tldraw__editor-with-inset-canvas"
              style={{
                width: DEVICE_DIMENSIONS[device].width,
                height: DEVICE_DIMENSIONS[device].height,
              }}
            >
              <Tldraw
                store={store}
                onMount={handleMount}
                components={TLDRAW_CONFIG.components}
                options={TLDRAW_CONFIG.options}
              />
            </div>
          </EditorWrapper>
        </EditorContainer>
      </RootContent>

      <DialogActions>
        <Button type="submit" variant="contained" color="primary">
          게시하기
        </Button>
      </DialogActions>
    </FormProvider>
  );
};

export default observer(EditorMode);

const RootContent = styled(DialogContent)({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const ControlBar = styled(Stack)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: theme.palette.background.neutral,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ControlBarInner = styled(Stack)({
  height: 56,
  padding: '0 16px',
});

const DeviceInfo = styled(Typography)({
  minWidth: 100,
});

const EditorContainer = styled(Stack)(({ theme }) => ({
  padding: 24,
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.background.neutral,
}));

const EditorWrapper = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: 'white',
}));
